(function(angular, _) {
    /**
     * @ngdoc controller
     * @name MonadSelectorController
     * @description Controller responsible for handling the monad selector
     */
    class MonadSelectorController {
        static get $inject() {
            return ['$scope', '$element', '$injector'];
        }

        constructor($scope, $element, $injector) {
            this.$scope = $scope;
            this.$element = $element;

            this.Monads = $injector.get('Monads');
            this.$window = $injector.get('$window');
            this.$timeout = $injector.get('$timeout');
            this.$document = $injector.get('$document');
            this.$rootScope = $injector.get('$rootScope');
            this.supportlinkFilter = $injector.get('$filter')('supportLink');
            this.monadSelectorService = $injector.get('monadSelectorService');

            /**
             * The search term to filter elements by
             * @type {string}
             */
            this.searchTerm = '';

            /**
             * Init optional object
             * @type {Object}
             */
            this.selectorHeading = {};

            /**
             * Optional tooltype handler
             * @type {Function}
             */
            this.getItemTooltip = angular.noop;

            /**
             * Init optional callback
             * @type {Function}
             */
            this.isDisabled = angular.noop;

            /**
             * List of monads
             * @type {Array}
             */
            this.listItems_ = [];

            /**
             * List of monad groups
             * @type {Array}
             */
            this.groupedItems_ = [];

            /**
             * Indicator used in the template to defer the rendering of the accordion
             * @type {boolean}
             */
            this.busy = true;

            /**
             * Map containing the watchers defined in the controller
             * @type {Object}
             */
            this.watchers = {};
        }

        $onInit() {
            this.onDocumentClick = this.onDocumentClick_.bind(this);

            /**
             * Indicates whether the component displays list or group view
             * TODO: why not regular boolean in this.listView?
             * @type {boolean}
             */
            this.isListView = _.toInteger(this.listView) !== 0;

            this.setLists_();
        }

        $postLink() {
            this.initVisibility_();

            this.setFocusOnSearch();

            this.$timeout(() => {
                // Makes the monad selector keyboard navigable
                this.$element.find('.selector-list-toggle-heading .accordion-toggle').attr('tabindex', 0);

                this.busy = false;
            }, 100);

            this.$element.on('$destroy', () => {
                this.$scope.$destroy();
            });

            this.$document.bind('click', this.onDocumentClick);

            this.watchers.onExternalHandlerToggle = this.$rootScope.$on(this.externalHandlerToggle, this.onExternalHandlerToggle.bind(this));
        }

        $onDestroy() {
            _.forEach(this.watchers, watcher => watcher());
            this.$document.unbind('click', this.onDocumentClick);
        }

        $onChanges(changelist) {
            const hasListChange = _.chain(changelist)
                                    .get('listItems.previousValue')
                                    .isArray()
                                    .value();
            if (hasListChange) {
                this.setLists_();
            }
        }

        /**
         * @ngdoc property
         * @name MonadSelectorController#onExternalHandlerToggle
         * @description Toggles monad selector from handler
         * @returns {MonadSelectorController}
         */
        onExternalHandlerToggle() {
            return this.setLists_().setFocusOnSearch();
        }

        /**
         * @ngdoc property
         * @name MonadSelectorController#keyboardActive
         * @description Keyboard navigation is active only when the element is visible
         * @returns {boolean}
         */
        get keyboardActive() {
            return this.$element[0].offsetHeight > 0;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#onDocumentClick_
         * @description Callback for document click; used to close panel on click 'outside'
         * @param {Event} e
         * @private
         */
        onDocumentClick_(e) {
            if (this.externalHandler || !this.isCollapsed) {
                return;
            }

            if (this.$element[0].contains(e.target)) {
                return;
            }

            this.isCollapsed = false;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#initVisibility_
         * @description Sets the isCollapsed property; setting visibility of main accordion
         * @returns {MonadSelectorController}
         * @private
         */
        initVisibility_() {
            this.isCollapsed = _.toString(this.selectorCollapse) === 'true';

            if (!this.externalHandler) {
                return this;
            }

            // Set the default isCollapsed to true when an extrnal handler
            // is present ignoring what was set.
            this.isCollapsed = true;

            if (!this.externalHandler.open) {
                this.$element.hide();
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#setFocusOnSearch
         * @description Will simply set focus on the search input field
         * @returns {MonadSelectorController}
         */
        setFocusOnSearch() {
            this.$timeout(() => {
                this.$element.find('input.form-control.input-sm').focus().select();
            }, 300);
            return this;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#setNormalLists_
         * @description Will generate a 'list' list and a 'group' list to be iterate over in the template
         * when regural monad selector view is on.
         * @returns {MonadSelectorController}
         * @private
         */
        setNormalLists_() {
            this.listItems_ = this.monadSelectorService.filterAndSort(this.listItems, this.searchTerm);
            this.groupedItems_ = _.chain(this.listItems_)
                                    .groupBy((monad) => {
                                        return monad.filterGroup;
                                    })
                                    .map((monads, name) => {
                                        const isOpen = _.chain(this.groupedItems_)
                                                        .find({name})
                                                        .get('isOpen', false)
                                                        .value();
                                        return {name, monads, isOpen};
                                    })
                                    .orderBy(
                                        [group => _.chain(group).get('name').toString().toLower().value()],
                                        ['asc']
                                    )
                                    .value();
            return this;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#setGroupedLists_
         * @description Will generate a 'list' list and a 'group' list to be iterate over in the template
         * when 'grouped' monad selector view is on.
         * @returns {MonadSelectorController}
         * @private
         */
        setGroupedLists_() {
            this.listItems_ = _.chain(this.listItems)
                                .map((monadType) => {
                                    const monads = this.monadSelectorService.filterAndSort(monadType.data, this.searchTerm);
                                    return _.chain(monadType)
                                            .pick(['id', 'title'])
                                            .set('monads', monads)
                                            .value();
                                })
                                .filter((monadType) => _.size(monadType.monads))
                                .value();
            this.groupedItems_ = _.chain(this.listItems_)
                                    .map((monadType) => {
                                        return _.chain(monadType)
                                                .get('monads')
                                                .groupBy('filterGroup')
                                                .map((group, name) => {
                                                    const monadTypes = _.chain(group)
                                                                        .groupBy((monad_) => {
                                                                            const arrangeType = _.get(monad_, 'arrangeType');
                                                                            const monad = this.Monads.monad(arrangeType);
                                                                            //@todo: Why default to dimension??? This is currently in production.
                                                                            const title = _.chain(monad).get('columnType', 'dimension').upperFirst().value();
                                                                            return `${title}s`;
                                                                        })
                                                                        .map((monads, title) => {
                                                                            return {title, monads};
                                                                        })
                                                                        .value();
                                                    return {name, monadTypes};
                                                })
                                                .flatten()
                                                .value();
                                    })
                                    .flatten()
                                    .groupBy('name')
                                    .map((groups, name) => {
                                        const monadTypes = _.chain(groups)
                                                            .map('monadTypes')
                                                            .flatten()
                                                            .value();

                                        const size = _.chain(monadTypes)
                                                        .map((monadType) => {
                                                            return monadType.monads.length;
                                                        })
                                                        .reduce((a, b) => a + b)
                                                        .value();

                                        const isOpen = _.chain(this.groupedItems_)
                                                        .find({name})
                                                        .get('isOpen', false)
                                                        .value();

                                        return {name, monadTypes, size, isOpen};
                                    })
                                    .orderBy(
                                        [group => _.chain(group).get('name').toString().toLower().value()],
                                        ['asc']
                                    )
                                    .value();
            return this;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#setLists_
         * @description Decides which list types to generate based on groupedSelector
         * @returns {MonadSelectorController}
         * @private
         */
        setLists_() {
            if (_.toString(this.groupedSelector) === 'true') {
                return this.setGroupedLists_();
            }
            return this.setNormalLists_();
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#onSearch
         * @description On search model change
         * @returns {MonadSelectorController}
         */
        onSearch() {
            this.setLists_();
            this.isListView = true;

            return this;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#onHeadingClick
         * @description Method exposed to selector-heading to allow setting focus on the search box :/
         * @param   {*} titleElement
         * @returns {boolean}
         */
        onHeadingClick(titleElement) {
            if (_.isNil(titleElement)) {
                return false;
            }

            this.setFocusOnSearch();

            return true;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#closePanelHeading_
         * @description Enabling closing of panel on item click when a panel header is present. Should be enabled
         * only on usages where the 'master' panel heading is enabled and configured.
         * @private
         */
        closePanelHeading_() {
            const panelHeadingElementScope = angular.element(this.$element.find('.panel-heading')[0]).scope();
            if (_.has(panelHeadingElementScope, 'heading')) {
                panelHeadingElementScope.isOpen = false;
                this.$element.css('height', 'auto');
            }
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#listClick
         * @description Click handler for 'list', bubbling down to list elements
         * @param {Event} event
         * @returns {Event} The original event
         */
        listClick(event) {
            const monad = _.get(angular.element(event.target).scope(), 'monad');

            if (!monad) {
                return event;
            }

            if (this.isDisabled && this.isDisabled({monad})) {
                return event;
            }

            const onItemLeftClick = _.get(this, 'clickHandlers.onItemLeftClick');

            if (_.isFunction(onItemLeftClick)) {
                onItemLeftClick({
                    analysisId: this.analysisId,
                    clickedItem: monad,
                    selectorIndex: this.selectorIndex,
                    event
                });
            }

            this.closePanelHeading_();

            return event;
        }

        /**
         * @ngdoc method
         * @name MonadSelectorController#helpClick
         * @description Click handler for help icon
         * @param {Event} event
         * * @param {Object} monad
         * @returns {boolean}
         */
        helpClick(event, monad) {
            const targetUrl = this.supportlinkFilter(_.get(monad, 'arrangeType'));
            this.$window.open(targetUrl);

            event.stopPropagation();

            return false;
        }

    }

    angular.module('wtApp.wtSelector').component('monadSelector', {
        controller: MonadSelectorController,
        template: ['$attrs', '$templateCache', ($attrs, $templateCache) => {
            const grouped = _.toString($attrs.groupedSelector) === 'true' ? '-grouped' : '';
            return $templateCache.get(`src/components/monad-selector/monad-selector${grouped}.component.html`);
        }],
        bindings: {
            /**
             * The analysis id
             * @type {string}
             */
            analysisId: '<?',

            /**
             * Map of click handling methods (i.e. right and single click)
             * @type {Object}
             */
            clickHandlers: '<',

            /**
             * Object with external handler options {"open": true/false, "toggle"}
             * @type {Object}
             */
            externalHandler: '<?',

            /**
             * List of items to display and group
             * @type {Array<Object>}
             */
            listItems: '<',

            /**
             * Optional id of toggle event triggered by external handler
             * @type {string}
             */
            externalHandlerToggle: '@?',

            /**
             * Optional init search key word
             * @type {string}
             */
            searchTerm: '@?',

            /**
             * Optionally indicates with which view to start.
             * @type {string}
             */
            listView: '@?',

            /**
             * Optionally indicates with which state to start.
             * @type {string}
             */
            selectorCollapse: '@?',

            /**
             * Optionally indicates even the list view is grouped an the grouped view has a deeper grouping.
             * @type {string}
             */
            groupedSelector: '@?',

            /**
             * Optional object with heading options {"remove": true/false, "clickable": true/false, "disabled": true/false}
             * @type {Object}
             */
            selectorHeading: '<?',

            /**
             * Optional parameter to be given when selector element in ng-repeat loop
             * @type {string}
             */
            selectorIndex: '<?',

            /**
             * Optional parameter for the title
             * @type {string}
             */
            selectorTitle: '<?',

            /**
             * Optional callback to check if element is disabled or not
             * @type {Function}
             */
            isDisabled: '&?',

            /**
             * Optional callback to display a tooltip
             * @type {Function}
             */
            getItemTooltip: '&?'
        }
    });
})(window.angular, window._);
